import { Injectable, inject } from "@angular/core";
import { DateTime } from "luxon";
import { type Observable } from "rxjs";
import type {
	Call,
	Comment,
	Inquiry,
	Lead,
	Message,
	Tour,
	TourEvent,
} from "src/app/schema";
import {
	AutopilotService,
	CognitoService,
	MessageService,
	WorkspaceService,
} from "src/app/services";

export type DateEvent = {
	__typename: "DateEvent";
	date: string;
};

export type ThreadItem =
	| Message
	| TourEvent
	| Inquiry
	| Call
	| Comment
	| DateEvent;

@Injectable({
	providedIn: "root",
})
export class CommunicationsService {
	private readonly _messageService = inject(MessageService);
	private readonly _cognitoService = inject(CognitoService);
	private readonly _autopilotService = inject(AutopilotService);
	private readonly _workspaceService = inject(WorkspaceService);

	public sendMessage(
		message: string,
		lead: Lead,
		channel: "Email" | "Text",
	): Observable<Message> {
		if (lead.autopilotRecommendation)
			return this._autopilotService.modify(lead.id, message);
		return this._messageService.sendMessage({
			leadId: lead.id,
			message,
			userId: this._cognitoService.user().id,
			propertyId: this._workspaceService.workspaceId(),
			channel,
		});
	}

	public processThread(
		comms: (Tour | Message | Inquiry | Call | Comment)[],
	): ThreadItem[] {
		const threadItems = {
			messages: comms.filter((c) => c.__typename === "Message"),
			tourEvents: (comms.filter((c) => c.__typename === "Tour") as Array<Tour>)
				.flatMap((t) => t.events)
				.map((e) => ({ ...e, __typename: "TourEvent" })),
			inquiries: comms.filter((c) => c.__typename === "Inquiry"),
			calls: comms.filter((c) => c.__typename === "Call"),
			comments: comms.filter((c) => c.__typename === "Comment"),
		};

		const thread = [
			...threadItems.messages,
			...threadItems.tourEvents,
			...threadItems.inquiries,
			...threadItems.calls,
			...threadItems.comments,
		] as ThreadItem[];

		const sortedThread = thread.sort((a, b) =>
			DateTime.fromISO(a.date).diff(DateTime.fromISO(b.date)).valueOf(),
		);
		return this._addDateEvents(sortedThread);
	}

	private _addDateEvents(thread: ThreadItem[]): ThreadItem[] {
		if (!thread.length) return [];
		let currentDate = DateTime.fromISO(thread[0].date).toFormat("y-LL-dd");
		const newThread = [...thread];
		for (let i = 1, n = 0; i < newThread.length; i++) {
			const date = DateTime.fromISO(newThread[i].date);
			if (date.toFormat("y-LL-dd") !== currentDate) {
				thread.splice(i + n++, 0, {
					__typename: "DateEvent",
					date: date.setZone("America/New_York").toFormat("DDDD"),
				});
			}
			currentDate = date.toFormat("y-LL-dd");
		}
		return thread;
	}
}
